import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { VueQueryPlugin } from '@tanstack/vue-query';
import { VueRendererMarkdown, enableKatex, enableMermaid, setDefaultMathOptions, setCustomComponents } from 'markstream-vue';

import 'katex/dist/katex.min.css';
// markstream-vue's complete default stylesheet (component layout + light/dark base
// palette). Imported ONCE globally here - was previously pulled in per-view
// (HomeView/HelpView/QuickAsk/SkillDetail), which code-split it and left the Studio
// conversation depending on load order. Must precede ./styles/tokens.css so our
// design-token retints (doubled-class scope) override the library base.
import 'markstream-vue/index.css';
import './styles/fonts.css';
import './styles/tokens.css';
import './styles/base.css';
import './styles/auth.css';
import './styles/scriptor.css';

import App from './App.vue';
import { router } from './router';
import { bootstrapTheme } from '@/stores/theme';
import Mention from '@/components/markdown/Mention.vue';
import Channel from '@/components/markdown/Channel.vue';
import Broadcast from '@/components/markdown/Broadcast.vue';
import { CitationChip, ICONS_KEY, configureMermaidRepair, HqMermaid, type HqIcons } from '@hq/chat-ui';
import { iconComp } from '@/lib/icon-component';

// Apply the chosen theme BEFORE mount so the first paint matches the
// user's preference - no white-flash for dark-mode users.
bootstrapTheme();

const app = createApp(App);
// Feed @hq/chat-ui its icon set so the shared CitationChip / Fold / result
// renderers stay icon-library-agnostic. iconComp adapts the app's central icon
// registry (lib/icons.ts) into the Component slots chat-ui expects.
app.provide(ICONS_KEY, {
  globe: iconComp('globe'), chat: iconComp('chat-circle'), brain: iconComp('brain'),
  externalLink: iconComp('arrow-square-out'), caretRight: iconComp('caret-right'),
  close: iconComp('x'), search: iconComp('magnifying-glass'),
  copy: iconComp('copy'), check: iconComp('check'),
} satisfies HqIcons);
app.use(createPinia());
app.use(router);
app.use(VueQueryPlugin);
// markstream custom components: Slack control tokens (`<mention>`,
// `<channel>`, `<broadcast>` - emitted by lib/slackMarkdown.ts) render
// as chips. Keyed by node.type; the mapping is global, so every
// <MarkdownRender> that lists these in its `custom-html-tags` prop
// (see SLACK_CUSTOM_TAGS) picks them up. Add a component here + a
// handler in slackMarkdown.ts to support a new token chip.
//
// `reference` overrides the built-in [n] citation node with CitationChip:
// it resolves [n] against the message's sources (provided per-message by
// CitationProvider) and pops a hover preview. Messages with no provider
// (docs, file previews) inject an empty list, so a stray [1] there falls
// back to a neutral marker.
app.use(VueRendererMarkdown, {
  components: { mention: Mention, channel: Channel, broadcast: Broadcast, reference: CitationChip },
});

// Diagrams in rendered markdown. Mermaid loads from the BUNDLED package via a
// dynamic import (never a CDN); it's heavy, so its import() lazy-splits into its own
// chunk that only loads the first time a ```mermaid fence renders. markstream forces
// mermaid's own light/dark theme and exposes no config hook, so we restyle the
// rendered SVG to the HQ palette in CSS (tokens.css,
// `.markstream-vue.markdown-renderer .mermaid-block-container`). Code highlighting is
// per-instance (code-renderer="shiki"), backed by the curated `^shiki$` alias.
enableMermaid(() => import('mermaid').then((m) => (m as { default?: unknown }).default ?? m));
// Same mermaid module powers @hq/chat-ui's auto-repair: a diagram that fails to
// parse gets its labels re-quoted and re-rendered via this loader (own instance).
configureMermaidRepair(() => import('mermaid'));
// Replace markstream's built-in MermaidBlockNode with HqMermaid, which owns the async
// render (source+theme cache, re-mount-safe). markstream's built-in re-creates itself
// under cold-load contention and its render-dedup cache orphans the in-flight render,
// leaving diagrams stuck blank. enableMermaid stays so markstream still recognizes the
// fence and routes it to our override. See agent-memory: "mermaid renders then disappears".
setCustomComponents({ mermaid: HqMermaid });

// KaTeX is ON: agents DO emit real LaTeX (inline `\(...\)` / `$x$`, display
// `$$...$$`). markstream treats a closed `$...$` pair as inline math and gives no way
// to disable just that delimiter - MathOptions exposes only commands/escapeExclamation/
// strictDelimiters (confirmed against the markstream-vue + stream-markdown-parser docs
// and the bundled parser; strictDelimiters still keeps `$...$`). HQ's content also uses
// `$` for currency ("$85,7 miljarder", "$965B"), so two amounts in one block would
// otherwise be parsed as one math span. We solve that at the SOURCE, not here:
// slackToMarkdown escapes a currency `$` (a `$` directly before a digit) to `\$`, so
// currency never tokenizes as math while real `\(...\)` / `$$...$$` math is untouched.
// escapeExclamation lets `\!` (negative thin space) survive instead of a literal.
enableKatex(() => import('katex').then((m) => (m as { default?: unknown }).default ?? m));
setDefaultMathOptions({ escapeExclamation: true });

app.mount('#app');
